/**
 * Historie eines Tickets aus den aggregierten Endpunkten eines Vorgangs bzw.
 * Teilvorgangs zusammenbauen -- #470 (Scheibe 1 aus #310).
 *
 * Die Funktionen standen woertlich so in TicketDetail.js. Sie haben keinen
 * Zustand und keine React-Abhaengigkeit, deshalb sind sie hier einzeln
 * testbar (ticketHistorie.test.js). #447 baut darauf auf.
 */

export const buildAggregatedHistory = (comments, emails, attachments, teamsMessages = []) => {
  // Map comments (from project/subproject history endpoint)
  const commentItems = comments.map((c, idx) => {
    const commentId = c.commentId || null;
    const mappedId = commentId ? `comment-${commentId}` : `agg-comment-${idx}`;
    return {
      id: mappedId,
      type: c.type || 'comment',
      timestamp: c.createdAt,
      sortTimestamp: new Date(c.createdAt),
      content: c.content,
      authorName: c.authorName,
      authorEmail: c.authorEmail,
      isInternal: c.isInternal || false,
      metadata: c.metadata || null,
      ticketId: c.ticketId || null,
      commentId: commentId,
      scope: c.scope || null,
      archived: !!c.archived
    };
  });

  // Map emails (from aggregated emails endpoint)
  const emailItems = emails.map((l, idx) => ({
    id: `agg-email-${l.emailId || idx}`,
    type: 'email',
    timestamp: l.createdAt,
    sortTimestamp: new Date(l.createdAt),
    content: l.email?.subject ? `E-Mail hinzugefügt: ${l.email.subject}` : 'E-Mail hinzugefügt',
    authorName: l.email?.fromName || l.email?.fromAddress || l.addedBy || 'Unbekannt',
    authorEmail: l.email?.fromAddress || null,
    isInternal: false,
    email: l.email || null,
    // #232: Richtung kommt jetzt auch aus dem aggregierten Endpoint; fehlt sie, bleibt sie
    // bewusst null (unbekannt) statt stumm als eingehend zu gelten.
    emailDirection: l.emailDirection || null,
    addedBy: l.addedBy,
    isOriginalEmail: !!l.isOriginalEmail,
    // #447: An welchem Ticket des Vorgangs die Mail haengt (das Loesen geht
    // gegen DIESES Ticket) und ob sie sich loesen laesst -- entschieden hat
    // das der Server (server/utils/mailZuordnung.js), hier wird nur
    // durchgereicht. Fehlt das Feld, gilt: nicht loesbar.
    ticketId: l.ticketId || null,
    loesbar: l.loesbar === true,
    // Anhänge der Mail wie im Ticket-Verlauf (#635); fehlt das Feld, gibt es keine.
    ...(Array.isArray(l.mailAnhaenge) && l.mailAnhaenge.length > 0 ? { mailAnhaenge: l.mailAnhaenge } : {})
  }));

  // Map Teams-Nachrichten (aggregierter Endpoint). Sortiert wird – wie beim
  // Ticket-eigenen Historien-Pfad – nach Erstellzeitpunkt der Nachricht selbst,
  // damit alte Nachrichten chronologisch korrekt einsortiert werden.
  const teamsItems = (teamsMessages || []).map((l, idx) => {
    const tm = l.teamsMessage || null;
    const sortBase = tm?.createdDateTime || l.createdAt;
    return {
      id: `agg-teams-${l.messageId || tm?.id || idx}`,
      type: 'teams_message',
      timestamp: l.createdAt,
      sortTimestamp: new Date(sortBase),
      content: tm?.plainTextBody
        ? `Teams-Nachricht hinzugefügt: ${String(tm.plainTextBody).substring(0, 100)}...`
        : 'Teams-Nachricht hinzugefügt',
      authorName: tm?.fromName || l.addedBy || 'Unbekannt',
      authorEmail: null,
      isInternal: false,
      teamsMessage: tm,
      addedBy: l.addedBy,
      isOriginalMessage: !!l.isOriginalMessage,
      ticketId: l.ticketId || null
    };
  });

  // Group attachments into history items similar to server grouping
  const attachmentItems = buildAttachmentHistoryItems(attachments);

  const all = [...commentItems, ...emailItems, ...teamsItems, ...attachmentItems];
  all.sort((a, b) => new Date(b.sortTimestamp || b.timestamp) - new Date(a.sortTimestamp || a.timestamp));
  return all;
};

export const buildAttachmentHistoryItems = (atts) => {
  if (!Array.isArray(atts) || atts.length === 0) return [];
  const sorted = [...atts].sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt));
  const groups = [];
  const FIVE_MIN = 5 * 60 * 1000;
  for (const att of sorted) {
    const attTime = new Date(att.createdAt);
    const uploader = att.uploadedBy || 'unknown';
    let placed = false;
    for (const group of groups) {
      if (group.uploader === uploader && Math.abs(attTime - group.baseTime) <= FIVE_MIN) {
        group.items.push(att);
        if (attTime < group.baseTime) group.baseTime = attTime;
        placed = true;
        break;
      }
    }
    if (!placed) {
      groups.push({ uploader, baseTime: attTime, items: [att] });
    }
  }
  return groups.map((g, idx) => ({
    id: `agg-attach-${idx}`,
    type: 'attachment',
    timestamp: g.baseTime,
    sortTimestamp: g.baseTime,
    uploadedBy: g.uploader,
    attachments: g.items.map(att => ({
      id: att.id,
      filename: att.filename,
      originalName: att.originalName,
      contentType: att.contentType,
      size: att.size,
      isImage: !!att.isImage,
      description: att.description,
      createdAt: att.createdAt,
      ticketId: att.ticketId
    }))
  }));
};

// Gibt es NACH dem Zeitpunkt einen Benutzer-Kommentar (type 'comment') eines
// anderen Autors? Dann ist der Eintrag "beantwortet" und bleibt, wie er ist.
// System-Eintraege und Mails zaehlen nicht.
const neuererFremderKommentar = (history, zeitpunkt, eigeneEmail) => (history || []).some(h => {
  if (h.type !== 'comment') return false;
  const hTime = new Date(h.sortTimestamp || h.timestamp);
  if (hTime <= zeitpunkt) return false;
  const hAuthor = (h.authorEmail || '').toLowerCase();
  return hAuthor && hAuthor !== eigeneEmail;
});

// Eigenen Kommentar bearbeiten/loeschen: nur, solange kein neuerer
// Kommentar eines anderen folgt. Aus Historie.js (canEditItem) hierher
// gezogen, damit die Mobilansicht dieselbe Regel benutzt.
export const darfKommentarBearbeiten = (item, history, benutzerEmail) => {
  if (!item || item.type !== 'comment') return false;
  const userEmail = (benutzerEmail || '').toLowerCase();
  const authorEmail = (item.authorEmail || '').toLowerCase();
  if (!userEmail || userEmail !== authorEmail) return false;
  const itemTime = new Date(item.sortTimestamp || item.timestamp);
  return !neuererFremderKommentar(history, itemTime, authorEmail);
};

// Anhaenge einer Upload-Gruppe loeschen: nur der Hochladende, und nur,
// solange kein neuerer Kommentar eines anderen folgt (Historie.js).
export const darfAnhaengeLoeschen = (item, history, benutzerEmail) => {
  const userEmailLower = (benutzerEmail || '').toLowerCase();
  const uploaderLower = (item?.uploadedBy || '').toLowerCase();
  const itemTime = new Date(item?.sortTimestamp || item?.timestamp);
  return !!userEmailLower && userEmailLower === uploaderLower && !neuererFremderKommentar(history, itemTime, userEmailLower);
};

// #508: „Transkribieren“ an Audio-Anhängen. Spiegelt den Server
// (POST /api/tickets/:id/transkription): Schalter `voicemailAi`, Ticket nicht
// abgeschlossen, Schreibrecht. Ohne Manager-Rolle sieht man nur zugewiesene
// Tickets (Revision 19.10.), Ticketworker dürfen sie also bearbeiten.
export const darfTranskribieren = ({ featureAn, ticket, dbUser }) => {
  if (!featureAn || !ticket || ticket.isArchived) return false;
  if (!dbUser || dbUser.isReadonly) return false;
  return !!(dbUser.isManager || dbUser.isTicketworker);
};
